<template>
  <b-card title="eKiosk KPI Past 3 Years Dashboard">
    <b-row class="mb-3">
      <b-col md="10">
        <label class="d-block d-flex align-items-center">
          KPI Name
          <kpi-process-flow-help class="ml-50" />
        </label>
        <b-form-select v-model="kpiType" :options="kpiOptions" />
      </b-col>
      <b-col md="2" class="d-flex align-items-end">
        <b-button variant="primary" :disabled="loading" block @click="load">
          {{ loading ? 'Loading...' : 'Load' }}
        </b-button>
      </b-col>
    </b-row>

    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <template v-if="yearlyItems.length > 0">
      <h4 class="mb-2 all-counter-summary-title">All Counter Summary (Past 3 Years)</h4>
      <b-row>
        <b-col v-for="item in yearlyItems" :key="item.year" lg="4" md="6" class="mb-2">
          <div :class="['h-100', isLiveSnapshot(item.snapshot) ? 'summary-card' : 'summary-card-finalized']">
            <div
              v-if="isLiveSnapshot(item.snapshot)"
              class="summary-year-heading summary-card-header d-flex align-items-center flex-wrap"
            >
              <h6 class="mb-0">
                <span class="mr-1" style="color: #fff;">
                  Year {{ item.year }}
                </span>
                <span
                  v-if="item.snapshot"
                  class="snapshot-status-badge"
                  :class="snapshotStatusClass(item.snapshot)"
                >
                  {{ snapshotStatusLabel(item.snapshot, item.year) }}
                </span>
              </h6>
            </div>
            <h6 v-else class="mb-2 d-flex align-items-center flex-wrap">
              <span class="mr-2">Year {{ item.year }}</span>
              <span
                v-if="item.snapshot"
                class="snapshot-status-badge"
                :class="snapshotStatusClass(item.snapshot)"
              >
                {{ snapshotStatusLabel(item.snapshot, item.year) }}
              </span>
            </h6>
            <div :class="{ 'summary-card-body': isLiveSnapshot(item.snapshot) }">
            <template v-if="item.snapshot">
              <div class="metric-list">
                <div class="metric-item">
                  <span class="metric-label">Average</span>
                  <span
                    class="metric-value"
                    :class="statusClassByTime(item.snapshot.allCounterSummary.avgTime, item.snapshot.kpiThreshold)"
                  >
                    {{ item.snapshot.allCounterSummary.avgTime }} min
                  </span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Target KPI</span>
                  <span class="metric-value">
                    {{ item.snapshot.kpiThreshold }} min
                  </span>
                </div>
 
                <div class="metric-item">
                  <span class="metric-label">&gt; KPI</span>
                  <span class="metric-value text-danger">
                    {{ item.snapshot.allCounterSummary.aboveKPI.count }}
                    ({{ item.snapshot.allCounterSummary.aboveKPI.percentage }}%)
                  </span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">&lt;= KPI</span>
                  <span class="metric-value text-success">
                    {{ item.snapshot.allCounterSummary.belowKPI.count }}
                    ({{ item.snapshot.allCounterSummary.belowKPI.percentage }}%)
                  </span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Total Orders</span>
                  <span class="metric-value">{{ item.snapshot.allCounterSummary.totalOrders }}</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Number of Counter in Dec {{ item.year }}</span>
                  <span class="metric-value">{{ item.snapshot.totalCountersInDecember || 0 }}</span>
                </div>
              </div>
            </template>
            <b-alert v-else show variant="light" class="mb-0">
              No yearly snapshot data for this KPI.
            </b-alert>
            </div>
          </div>
        </b-col>
      </b-row>

      <h5 class="mb-2 mt-1">By Counter (Per Year)</h5>
      <div v-for="item in yearlyItems" :key="`counter-${item.year}`" class="mb-1">
        <h6 class="mb-2 d-flex align-items-center flex-wrap">
          <span class="mr-2">Year {{ item.year }}</span>
          <span
            v-if="item.snapshot"
            class="snapshot-status-badge"
            :class="snapshotStatusClass(item.snapshot)"
          >
            {{ snapshotStatusLabel(item.snapshot, item.year) }}
          </span>
        </h6>
        <b-row v-if="item.snapshot && item.snapshot.counters && item.snapshot.counters.length > 0">
          <b-col
            v-for="(counter, cIdx) in item.snapshot.counters"
            :key="`${item.year}-counter-${cIdx}-${counter.counterName || 'unnamed'}`"
            lg="4"
            md="6"
            class="mb-4"
          >
            <b-card class="counter-card h-100">
              <div class="counter-card-header d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0 text-truncate counter-card-name" :title="counter.counterName">
                  {{ counter.counterName || 'Unnamed Counter' }}
                </h6>
                <span class="kpi-param-chip flex-shrink-0 ml-2">{{ selectedKpiLabel }}</span>
              </div>
              <div class="metric-list">
                <div class="metric-item">
                  <span class="metric-label">Average</span>
                  <span class="metric-value" :class="statusClassByTime(counter.avgTime, item.snapshot.kpiThreshold)">
                    {{ counter.avgTime }} min
                  </span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Target KPI</span>
                  <span class="metric-value">
                    {{ item.snapshot.kpiThreshold }} min
                  </span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">&gt; KPI</span>
                  <span class="metric-value text-danger">
                    {{ counter.aboveKPI.count }} ({{ counter.aboveKPI.percentage }}%)
                  </span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">&lt;= KPI</span>
                  <span class="metric-value text-success">
                    {{ counter.belowKPI.count }} ({{ counter.belowKPI.percentage }}%)
                  </span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Total Orders</span>
                  <span class="metric-value">{{ counter.totalOrders }}</span>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>
        <b-alert v-else show variant="light">
          No counter data available for year {{ item.year }}.
        </b-alert>
      </div>
    </template>
  </b-card>
</template>

<script>
import KpiProcessFlowHelp from './kpi-process-flow-help.vue';

export default {
  components: {
    KpiProcessFlowHelp,
  },
  data() {
    return {
      kpiType: 'QUEUE_TIME',
      yearlyItems: [],
      loadedKpiType: null,
      loading: false,
      error: '',
    };
  },
  computed: {
    kpiOptions() {
      return (this.$store.state.kioskKpi.masterItems || [])
        .filter((item) => item.isActive)
        .map((item) => ({ value: item.kpiName, text: item.displayName }));
    },
    selectedKpiLabel() {
      if (!this.loadedKpiType) return '';
      const found = this.kpiOptions.find((option) => option.value === this.loadedKpiType);
      return found ? found.text : this.loadedKpiType;
    },
  },
  async mounted() {
    document.title = 'eKiosk KPI Past 3 Years Dashboard | RSP';
    await this.$store.dispatch('kioskKpi/fetchKpiMaster');
    this.load();
  },
  methods: {
    isLiveSnapshot(snapshot) {
      return snapshot && !snapshot.isFinalized;
    },
    formatFinalizedDate(date) {
      if (!date) return '';
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Singapore',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(new Date(date));
    },
    snapshotStatusLabel(snapshot, year) {
      if (!snapshot) return '';
      if (!snapshot.isFinalized) return 'Live';
      let finalizedDate = this.formatFinalizedDate(snapshot.finalizedAt);
      if (!finalizedDate) {
        const fallbackYear = snapshot.year || year;
        if (fallbackYear) {
          finalizedDate = `31 Dec ${fallbackYear}`;
        }
      }
      return finalizedDate ? `Finalized at ${finalizedDate}` : 'Finalized';
    },
    snapshotStatusClass(snapshot) {
      if (!snapshot) return '';
      return snapshot.isFinalized ? 'snapshot-badge-finalized' : 'snapshot-badge-live';
    },
    statusClassByTime(minutes, thresholdMinutes) {
      return Number(minutes) > Number(thresholdMinutes) ? 'text-danger' : 'text-success';
    },
    async load() {
      this.error = '';
      this.loading = true;
      try {
        const result = await this.$store.dispatch('kioskKpi/fetchKpiPast3Years', {
          toDate: new Date().toISOString().slice(0, 10),
          kpiType: this.kpiType,
        });
        this.yearlyItems = result.data || [];
        this.loadedKpiType = this.kpiType;
      } catch (err) {
        console.error('Failed to load past 3 years dashboard', err);
        this.error =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          'Failed to load past 3 years dashboard';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.all-counter-summary-title {
  font-weight: bold;
  color: #000;
  padding-left: 0.75rem;
  border-left: 8px solid #40aee5;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
}

.modern-section-title {
  font-weight: 700;
  color: #2d3748;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
}

.modern-section-title::before {
  content: '';
  display: inline-block;
  width: 12px;
  height: 12px;
  background-color: #40aee5;
  border-radius: 50%;
  margin-right: 10px;
}

.summary-card {
  background-color: #f5f7fc;
  border: 1px solid #40aee5;
  border-radius: 0.75rem;
  box-shadow: 0 4px 15px rgba(64, 174, 229, 0.15);
  padding: 0;
  overflow: hidden;
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.summary-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.06), 0 4px 10px rgba(0, 0, 0, 0.03);
}

.summary-card-header {
  background-color: #40aee5;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #40aee5;
}

.summary-card-body {
  padding: 1.5rem;
  background-color: #f5f7fc;
}

.summary-card-finalized {
  background-color: #f5f7fc;
  border: 1px solid #d9d9d9;
  border-top: 5px solid #40aee5;
  border-radius: 0.75rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02);
  padding: 1.5rem;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.summary-card-finalized:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.06), 0 4px 10px rgba(0, 0, 0, 0.03);
  border-color: #d9d9d9;
  border-top-color: #40aee5;
}

.counter-card {
  border: 1px solid #d9d9d9;
  border-top: 5px solid #40aee5;
  border-radius: 0.75rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background-color: #ffffff;
}

.counter-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.06), 0 4px 10px rgba(0, 0, 0, 0.03);
  border-color: #d9d9d9;
  border-top-color: #40aee5;
}

.counter-card-header {
  gap: 0.5rem;
}

.counter-card-name {
  font-weight: 600;
}

.kpi-param-chip {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  border-radius: 0.358rem;
  background-color: #edf3ff;
  color: #40aee5;
}

.modern-badge {
  font-size: 0.75rem;
  padding: 0.4em 0.6em;
  font-weight: 600;
  border-radius: 0.4rem;
  background-color: #f8fafc;
}

.modern-progress {
  border-radius: 10px;
  background-color: #edf2f7;
  overflow: hidden;
}

.modern-progress .progress-bar {
  transition: width 0.6s ease;
}

.border-top-dashed {
  border-top: 1px dashed #edf2f7;
}

.snapshot-status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  border-radius: 0.358rem;
}

.snapshot-badge-live {
  background-color: #6dbb4c;
  color: #fff;
}

.snapshot-badge-finalized {
  background-color: #d9d9d9;
  color: #545454;
}

.metric-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.metric-item {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #dce3f0;
}

.metric-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.metric-label {
  color: #6c757d;
  font-size: 12px;
}

.metric-value {
  font-weight: 600;
}
</style>
